import { getRouteStart } from "../helpers/route-start";
import { RoutesMinAndMaxStartTime } from "../helpers/timetable";
import useRoutePlannerStore from "../../../../state/route-planner-store";

type CurrentTimeIndicatorProps = {
  timelineDate?: string;
  routesMinAndMaxStartTime: RoutesMinAndMaxStartTime;
  totalTimelineMinutes: number;
  totalTableItems: number;
};

const CurrentTimeIndicator = ({
  timelineDate,
  routesMinAndMaxStartTime,
  totalTimelineMinutes,
  totalTableItems,
}: CurrentTimeIndicatorProps) => {
  const min = routesMinAndMaxStartTime?.min;
  // Use centralized synced time from store - this ensures all components
  // update at exactly the same moment when the minute changes
  const currentTime = useRoutePlannerStore((state) => state.syncedCurrentTime);

  const startTime = min
    ? getRouteStart(min, timelineDate ?? currentTime.toISODate()!)
    : null;

  // Calculate end time based on start time + total timeline duration
  const endTime = startTime
    ? startTime.plus({ minutes: totalTimelineMinutes })
    : null;

  // If min or max is invalid, or current time is out of range, don't render the indicator
  if (
    !startTime?.isValid ||
    !endTime ||
    currentTime < startTime ||
    currentTime > endTime
  ) {
    return null;
  }

  // Calculate the start of the current minute (round down to minute boundary)
  const currentMinuteStart = currentTime.startOf("minute");
  const elapsedMinutes = currentMinuteStart.diff(startTime, "minutes").minutes;

  // Calculate position as a percentage based on the start of the current minute
  // Position should align with address boxes which are positioned at the START of their time slots
  const currentPosition = Math.max(
    0,
    Math.min(100, (elapsedMinutes / totalTimelineMinutes) * 100),
  ); // Clamp between 0 and 100

  return (
    <div
      data-testid="current-time-indicator"
      style={{
        position: "absolute",
        top: 30,
        left: `${currentPosition}%`,
        height: `${totalTableItems * 35}px`,
        width: "1px",
        backgroundColor: "#3FBF9E",
        transform: "translateX(-50%)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "-5px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          backgroundColor: "#3FBF9E",
        }}
      />
    </div>
  );
};

export default CurrentTimeIndicator;
